import { AIS_DIFFERENCE_INTERVALS, type AisDifferenceIntervals, type AisDifferenceLevel, aisDifferenceLevel } from '@/domain/aisDifference'
import { barcelonaMidnights } from '@/domain/dateTime'

import type { Muelle } from '../domain/scales'
import { type EventMarker, eventMarkers } from './summarise'
import type { EventRow } from './types'

export interface AisMark {
  at: string
  position: number
  differenceS: number | null
  level: AisDifferenceLevel | null
}

export interface AisMarks {
  arrival: AisMark
  departure: AisMark | null
  otherMuelle: Muelle | null
  arrivalFirstSeen: boolean
}

export interface GanttBar {
  marker: EventMarker
  from: number
  to: number
  open: boolean
  ais: AisMarks | null
}

export interface GanttChart {
  start: number
  end: number
  bars: GanttBar[]
  rows: GanttBar[][]
  days: { at: number; position: number }[]
  now: number | null
}

const MAX_DAYS = 12
const HOUR = 3_600_000

export const ganttChart = (
  events: readonly EventRow[],
  now: number,
  intervals: AisDifferenceIntervals = AIS_DIFFERENCE_INTERVALS,
): GanttChart | null => {
  const markers = eventMarkers(events, now)
  if (markers.length === 0) return null
  const checks = new Map(
    events.flatMap(event => (event.type === 'PLACE' && event.ais !== null ? [[event.id, { ais: event.ais, muelle: event.muelle }]] : [])),
  )
  const aisTimes = [...checks.values()].flatMap(({ ais }) => [
    Date.parse(ais.arrival),
    ...(ais.departure === null ? [] : [Date.parse(ais.departure)]),
  ])
  const times = [...markers.flatMap(m => [Date.parse(m.startTime), ...(m.endTime === null ? [] : [Date.parse(m.endTime)])]), ...aisTimes]
  const overdue = (m: EventMarker): boolean =>
    m.type === 'PLACE' && m.progress !== 'DONE' && m.endTime !== null && Date.parse(m.endTime) < now
  const runsOn = (m: EventMarker): boolean => m.endTime === null || overdue(m)
  const berthed = markers.some(m => m.type === 'PLACE' && runsOn(m) && Date.parse(m.startTime) <= now)
  const start = Math.min(...times)
  const latest = Math.max(...times, ...(berthed ? [now] : []))
  const end = Math.max(latest, start + HOUR)
  const position = (at: number): number => Math.min(1, Math.max(0, (at - start) / (end - start)))

  const mark = (at: string, differenceS: number | null): AisMark => ({
    at,
    position: position(Date.parse(at)),
    differenceS,
    level: differenceS === null ? null : aisDifferenceLevel(differenceS, intervals),
  })
  const bars = markers.map((marker): GanttBar => {
    const from = Date.parse(marker.startTime)
    const barEnd = (): number => {
      if (overdue(marker)) return now
      if (marker.endTime !== null) return Date.parse(marker.endTime)
      return now > from ? Math.min(now, end) : end
    }
    const to = barEnd()
    const check = marker.type === 'PLACE' ? checks.get(marker.id) : undefined
    const ais = check && {
      arrival: mark(check.ais.arrival, check.ais.arrivalDifferenceS),
      departure: check.ais.departure === null ? null : mark(check.ais.departure, check.ais.departureDifferenceS),
      otherMuelle: check.ais.muelleAgrees === false ? check.ais.muelle : null,
      arrivalFirstSeen: check.ais.arrivalObserved === false,
    }
    return { marker, from: position(from), to: position(to), open: runsOn(marker), ais: ais ?? null }
  })
  const rows: GanttBar[][] = []
  let marpols: GanttBar[] | undefined
  let bombeos: GanttBar[][] = []
  for (const bar of bars) {
    if (bar.marker.type === 'BOMBEO') bombeos.push([bar])
    else if (bar.marker.type === 'PLACE') {
      rows.push(...bombeos, [bar])
      bombeos = []
    } else if (marpols) marpols.push(bar)
    else {
      marpols = [bar]
      rows.push(marpols)
    }
  }
  rows.push(...bombeos)
  const midnights = barcelonaMidnights(start, end)
  const step = Math.max(1, Math.ceil(midnights.length / MAX_DAYS))
  const days = midnights.filter((_, index) => index % step === 0).map(at => ({ at, position: position(at) }))
  return { start, end, bars, rows, days, now: now >= start && now <= end ? position(now) : null }
}
